import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { AlertTriangle, Clock, CheckCircle2, XCircle, Calendar, Check, Download, Eye, FileText, Loader2, MessageSquare, Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  none: { label: 'Aucun justificatif', icon: AlertTriangle, classes: 'bg-slate-100 text-slate-600 border-slate-300' },
  pending: { label: 'En attente', icon: Clock, classes: 'bg-amber-50 text-amber-800 border-amber-300' },
  approved: { label: 'Validé', icon: CheckCircle2, classes: 'bg-emerald-50 text-emerald-800 border-emerald-300' },
  rejected: { label: 'Rejeté', icon: XCircle, classes: 'bg-rose-50 text-rose-800 border-rose-300' },
};

const formatSize = (bytes) => {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / 1048576).toFixed(1)} Mo`;
};

export const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.none;
  const Icon = cfg.icon;
  return (
    <Badge
      className={`gap-1 border ${cfg.classes} text-[10px] font-semibold uppercase tracking-wide`}
      data-testid={`qualif-status-${status}`}
    >
      <Icon className="h-3 w-3" />
      {cfg.label}
    </Badge>
  );
};

/** Sous-composant : ligne d'un justificatif uploadé */
export const JustificatifRow = ({ doc, membreId, token, canReview, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const fetchBlob = async () => {
    const res = await fetch(
      `${API}/api/admin/membres/${membreId}/qualifications/${doc.id}/file`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.blob();
  };

  const handleView = async () => {
    try {
      const blob = await fetchBlob();
      window.open(URL.createObjectURL(blob), '_blank');
    } catch {
      toast.error('Impossible d\'ouvrir le fichier');
    }
  };

  const handleDownload = async () => {
    try {
      const blob = await fetchBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.original_filename || doc.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Téléchargement impossible');
    }
  };

  const handleApprove = async () => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/api/admin/membres/${membreId}/qualifications/${doc.id}/approve`,
        { comment: 'OK' },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Justificatif validé');
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'approbation');
    } finally {
      setBusy(false);
    }
  };

  const submitReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Motif obligatoire');
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/api/admin/membres/${membreId}/qualifications/${doc.id}/reject`,
        { comment: rejectReason.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Justificatif rejeté');
      setRejectOpen(false);
      setRejectReason('');
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors du rejet');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Supprimer définitivement ce justificatif ?')) return;
    setBusy(true);
    try {
      await axios.delete(
        `${API}/api/admin/membres/${membreId}/qualifications/${doc.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Justificatif supprimé');
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur suppression');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div
        className="flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-white p-2.5 text-xs hover:border-slate-300 transition-colors"
        data-testid={`justif-row-${doc.id}`}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <FileText className="h-4 w-4 text-slate-500 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="font-medium text-slate-800 truncate">
              {doc.original_filename || doc.filename}
            </div>
            <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-500">
              <span>{formatSize(doc.file_size)}</span>
              <span>·</span>
              <span>{new Date(doc.uploaded_at).toLocaleDateString('fr-FR')}</span>
              {doc.valid_until && (
                <>
                  <span>·</span>
                  <span className="flex items-center gap-0.5">
                    <Calendar className="h-3 w-3" /> Valide jusqu'au {doc.valid_until}
                  </span>
                </>
              )}
            </div>
            {doc.status === 'rejected' && doc.review_comment && (
              <div className="mt-1 flex items-start gap-1 text-[10px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-1.5 py-1">
                <MessageSquare className="h-3 w-3 mt-0.5 shrink-0" />
                <span>{doc.review_comment}</span>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <StatusBadge status={doc.status} />
          <Button
            size="sm" variant="ghost"
            onClick={handleView}
            className="h-7 w-7 p-0"
            title="Visualiser"
            data-testid={`justif-view-${doc.id}`}
          >
            <Eye className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="sm" variant="ghost"
            onClick={handleDownload}
            className="h-7 w-7 p-0"
            title="Télécharger"
            data-testid={`justif-download-${doc.id}`}
          >
            <Download className="h-3.5 w-3.5" />
          </Button>
          {canReview && doc.status !== 'approved' && (
            <Button
              size="sm" variant="ghost"
              onClick={handleApprove}
              disabled={busy}
              className="h-7 w-7 p-0 text-emerald-600 hover:bg-emerald-50"
              title="Approuver"
              data-testid={`justif-approve-${doc.id}`}
            >
              <Check className="h-3.5 w-3.5" />
            </Button>
          )}
          {canReview && doc.status !== 'rejected' && (
            <Button
              size="sm" variant="ghost"
              onClick={() => setRejectOpen(true)}
              disabled={busy}
              className="h-7 w-7 p-0 text-rose-600 hover:bg-rose-50"
              title="Rejeter"
              data-testid={`justif-reject-${doc.id}`}
            >
              <XCircle className="h-3.5 w-3.5" />
            </Button>
          )}
          <Button
            size="sm" variant="ghost"
            onClick={handleDelete}
            disabled={busy}
            className="h-7 w-7 p-0 text-slate-500 hover:bg-rose-50 hover:text-rose-600"
            title="Supprimer"
            data-testid={`justif-delete-${doc.id}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Dialog motif de rejet */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="h-4 w-4 text-rose-600" /> Rejeter ce justificatif
            </DialogTitle>
            <DialogDescription>
              Le motif est obligatoire et sera visible par les administrateurs.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Ex : document illisible, expiré, ne correspond pas à la qualification..."
            rows={4}
            data-testid={`reject-reason-${doc.id}`}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)} disabled={busy}>
              Annuler
            </Button>
            <Button
              onClick={submitReject}
              disabled={busy || !rejectReason.trim()}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid={`confirm-reject-${doc.id}`}
            >
              {busy && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
              Confirmer le rejet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

